import React, { useEffect, useRef } from 'react';
import { mountWidget, SIZES } from '../../widget/widget-ui';

/**
 * Live preview for the Appearance page.
 *
 * This mounts the SAME module the customer's visitors get — widget-ui.ts —
 * with a stubbed transport instead of the real one. It is not a mockup that
 * approximates the widget; it is the widget, so the two cannot drift. The
 * dashboard previously rendered EnhancedChatWidget.jsx while customers got
 * backend/public/widget.js, and keeping those two in sync by hand is what ate
 * most of the June sessions.
 *
 * Everything visual belongs in widget-ui.ts. Nothing in this file should ever
 * style the widget.
 */

// Bot text goes through formatMd(), which HTML-escapes before applying markdown
// patterns. Write real characters here — an HTML entity would render literally.
const DEMO_REPLY =
  "Thanks for asking! I answer from **your** knowledge sources, so once you've " +
  "added a few I'll reply with real details from your business.";

/** Rough think-time before the first token, so the typing indicator is visible. */
const THINK_MS = 450;
/** Per-word delay while streaming, tuned to look like the real model's pace. */
const WORD_MS = 28;

export default function ChatPreview({ settings, className = '' }) {
  const hostRef = useRef(null);
  const handleRef = useRef(null);
  // Read through a ref so the fake transport never closes over a stale render.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Mount once. Settings changes go through handle.update() so the preview keeps
  // its conversation state instead of remounting — and losing the visitor's
  // messages — on every keystroke in the form.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;

    let alive = true;

    handleRef.current = mountWidget(el, settingsRef.current, {
      embedded: true,
      async send(_text, _history, onToken) {
        await new Promise((r) => setTimeout(r, THINK_MS));
        for (const word of DEMO_REPLY.split(' ')) {
          if (!alive) break;
          await new Promise((r) => setTimeout(r, WORD_MS));
          onToken(word + ' ');
        }
        return { success: true };
      },
    });

    return () => {
      alive = false;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, []);

  useEffect(() => {
    handleRef.current?.update(settings);
  }, [settings]);

  const [width, height] = SIZES[settings?.widget_size] || SIZES.medium;

  return (
    <div className={className}>
      {/* Soft neutral ground, not white. The widget's own surface is white in
          light mode and its shadow is what separates it from the page — both
          disappear against a white backdrop. */}
      <div
        className="flex items-start justify-center rounded-2xl p-6"
        style={{ background: '#eef0f4' }}
      >
        <div
          ref={hostRef}
          style={{
            // Exactly the panel size: embedded mode drops the launcher and
            // gives the panel the full height, so reserving LAUNCHER_STACK here
            // would just add dead space under it.
            width,
            height,
            transition: 'width .2s ease, height .2s ease',
          }}
        />
      </div>
      <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
        The real widget, not a mockup &mdash; this is exactly what visitors see.
      </p>
    </div>
  );
}
